import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
} from "@headlessui/react";
import Cropper from "react-easy-crop";
import type { Area, Point } from "react-easy-crop";
import { Loader2, X } from "lucide-react";
import { getCroppedImg, type PixelCrop } from "./getCroppedImg";
import type { CropConstraint } from "./cropConstraints";

interface CropModalProps {
  open: boolean;
  onClose: () => void;
  /** Source image URL to crop */
  imageSrc: string;
  /** Constraint from the registry */
  constraint: CropConstraint;
  /** Initial crop state for re-editing */
  initialCrop?: { x: number; y: number; zoom: number };
  /** Called with the cropped blob and crop metadata */
  onApply: (blob: Blob, cropArea: PixelCrop, zoom: number) => void;
}

export function CropModal({
  open,
  onClose,
  imageSrc,
  constraint,
  initialCrop,
  onApply,
}: CropModalProps) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when the modal opens
  useEffect(() => {
    if (open) {
      setCrop(
        initialCrop ? { x: initialCrop.x, y: initialCrop.y } : { x: 0, y: 0 },
      );
      setZoom(initialCrop?.zoom ?? 1);
      setCroppedArea(null);
      setCroppedAreaPixels(null);
      setIsProcessing(false);
      setError(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCropComplete = useCallback(
    (croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedArea(croppedArea);
      setCroppedAreaPixels(croppedAreaPixels);
    },
    [],
  );

  const tooSmall =
    croppedAreaPixels !== null &&
    (croppedAreaPixels.width < constraint.minWidth ||
      croppedAreaPixels.height < constraint.minHeight);

  const handleApply = useCallback(async () => {
    if (!croppedAreaPixels || tooSmall) return;
    setIsProcessing(true);
    setError(null);
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
      onApply(blob, croppedAreaPixels, zoom);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Crop failed");
    } finally {
      setIsProcessing(false);
    }
  }, [croppedAreaPixels, tooSmall, imageSrc, onApply, zoom, onClose]);

  return (
    <Dialog open={open} onClose={onClose} className="relative z-[210]">
      <DialogBackdrop className="fixed inset-0 bg-ink/60 backdrop-blur-sm" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="bg-paper-hi rounded-[14px] shadow-lift w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5 hair-b shrink-0">
            <h2 className="font-display text-[16px] font-semibold">
              Crop image
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="w-7 h-7 rounded hover:bg-paper-2 flex items-center justify-center text-ink-4"
              aria-label="Close"
            >
              <X size={14} strokeWidth={2} />
            </button>
          </div>

          {/* Body: crop area + sidebar */}
          <div className="flex flex-1 min-h-0">
            {/* Crop area */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="relative flex-1 min-h-[320px] bg-[#1a1a1a]">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={constraint.aspect}
                  cropShape={constraint.shape === "round" ? "round" : "rect"}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  showGrid
                />
              </div>

              {/* Zoom slider */}
              <div className="flex items-center gap-3 px-5 py-3 hair-t">
                <span className="text-[11px] font-semibold text-ink-4 uppercase tracking-wide">
                  Zoom
                </span>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  aria-label="Zoom level"
                  className="flex-1 accent-tide h-1"
                />
                <span className="text-[11px] font-mono text-ink-4 w-10 text-right">
                  {zoom.toFixed(1)}x
                </span>
              </div>
            </div>

            {/* Sidebar */}
            <div className="w-[240px] flex-none flex flex-col border-l border-ink-6 bg-paper">
              <div className="flex-1 px-4 py-4 space-y-4 overflow-y-auto">
                {/* Constraint label */}
                <div>
                  <span className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-1">
                    Format
                  </span>
                  <span className="text-[12px] text-ink-3 leading-relaxed">
                    {constraint.label}
                  </span>
                </div>

                {/* Live preview -- shows the actual crop region, driven by
                    react-easy-crop's percentage croppedArea via CSS background
                    (no canvas read, so it is unaffected by cross-origin CDN CORS) */}
                {croppedArea && croppedAreaPixels && (
                  <div>
                    <span className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-2">
                      Live preview
                    </span>
                    <div
                      className="w-full overflow-hidden bg-paper-2"
                      style={{
                        borderRadius: constraint.shape === "round" ? "50%" : "8px",
                        aspectRatio: constraint.aspect
                          ? String(constraint.aspect)
                          : `${croppedAreaPixels.width} / ${croppedAreaPixels.height}`,
                        backgroundImage: `url("${imageSrc}")`,
                        backgroundRepeat: "no-repeat",
                        // Scale the image so the crop region fills the box...
                        backgroundSize: `${10000 / croppedArea.width}% ${10000 / croppedArea.height}%`,
                        // ...then pan to the crop's origin (guard the no-pan axis)
                        backgroundPosition: `${
                          croppedArea.width >= 100
                            ? 50
                            : (croppedArea.x / (100 - croppedArea.width)) * 100
                        }% ${
                          croppedArea.height >= 100
                            ? 50
                            : (croppedArea.y / (100 - croppedArea.height)) * 100
                        }%`,
                      }}
                    />
                    <span className="block text-[10.5px] text-ink-5 mt-1.5">
                      {croppedAreaPixels.width} x {croppedAreaPixels.height}px
                    </span>
                  </div>
                )}

                {/* Too-small warning */}
                {tooSmall && (
                  <p className="text-[11px] text-rust font-medium leading-relaxed">
                    Zoom out or use a larger image -- minimum{" "}
                    {constraint.minWidth}x{constraint.minHeight}px
                  </p>
                )}

                {error && (
                  <p className="text-[11px] text-rust font-medium">{error}</p>
                )}
              </div>

              {/* Footer buttons */}
              <div className="flex flex-col gap-2 px-4 py-3.5 hair-t">
                <button
                  type="button"
                  onClick={handleApply}
                  disabled={!croppedAreaPixels || tooSmall || isProcessing}
                  className="w-full flex items-center justify-center gap-2 rounded-[9px] bg-tide px-4 py-2 text-[12px] font-semibold text-white hover:bg-tide-d disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    "Apply crop"
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full rounded-[9px] border border-ink-6 bg-paper px-4 py-2 text-[12px] font-semibold text-ink-3 hover:bg-paper-2 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
